import { useEffect, useRef } from "react";
import {
  Palette,
  Home,
  Building2,
  Hammer,
  KeyRound,
} from "lucide-react";

const services = [
  {
    icon: Palette,
    title: "Interior Design & Decoration",
    description: "Curated interiors that blend aesthetics with comfort and functionality.",
  },
  {
    icon: Home,
    title: "Residential Construction",
    description: "Custom homes built with quality materials and meticulous attention to detail.",
  },
  {
    icon: Building2,
    title: "Commercial Construction",
    description: "Functional commercial spaces designed for productivity and lasting value.",
  },
  {
    icon: Hammer,
    title: "Renovation & Remodeling",
    description: "Breathe new life into existing spaces with expert renovation services.",
  },
  {
    icon: KeyRound,
    title: "Turnkey Solutions",
    description: "End-to-end project delivery — move in without lifting a finger.",
  },
];

export default function Services() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll(".animate-on-scroll");
            elements.forEach((el) => el.classList.add("visible"));
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      ref={sectionRef}
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#FDFDFC]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="animate-on-scroll mb-16 md:mb-20">
          <div className="w-12 h-[2px] bg-gold mb-6" />
          <h2
            data-testid="services-heading"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-charcoal tracking-[-0.02em]"
          >
            Our Services
          </h2>
          <p className="font-body text-base md:text-lg text-[#555555] mt-4 max-w-xl font-light">
            Comprehensive construction and design solutions tailored to your vision.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, i) => {
            const Icon = service.icon;
            return (
              <div
                key={service.title}
                data-testid={`service-card-${i}`}
                className={`animate-on-scroll delay-${(i + 1) * 100} group border border-[#E2E2E0] bg-white p-8 md:p-10 transition-all duration-500 hover:border-charcoal hover:bg-[#F2F2F0] cursor-default ${
                  i >= 3 ? "lg:col-span-1 lg:last:col-start-2" : ""
                }`}
              >
                <div className="w-12 h-12 flex items-center justify-center border border-[#E2E2E0] group-hover:border-gold group-hover:text-gold transition-all duration-300 mb-6">
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg md:text-xl font-semibold text-charcoal mb-3 tracking-[-0.01em]">
                  {service.title}
                </h3>
                <p className="font-body text-sm text-[#666666] leading-relaxed font-light">
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
